'use client';

/**
 * ObeyaAttentionCard — list of red/amber items needing action.
 *
 * Mirrors the "Q1: Needs Attention" quadrant in `ObeyaOverview`.
 *
 * @see klyde_ipe/components/widgets/obeya-widget.tsx (legacy)
 */

import { useMemo } from 'react';
import { AlertCircle, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import { text, surfaceBase } from '@/lib/design/tokens.x';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';
import { useCardExport } from '@/lib/design/card-export';
import { useCardIdentity } from '@/lib/design/card-chrome';
import { useObeyaProjectData } from './use-obeya-project-data';
import { CardShell } from '../card-shell';

export function ObeyaAttentionCard() {
  const { data, isLoading, projectId, queryKey } = useObeyaProjectData();
  const { contentKey } = useCardIdentity();
  const selectNode = useGraphSelectionStore((s) => s.selectNode);

  // Scene export — register this rail card so mode1's rail contributes to
  // the capture envelope. Called before the early returns (Rules of Hooks);
  // content_key comes from context (shared canvas+rail card).
  useCardExport(useMemo(() => {
    const needsAttention = data
      ? [...data.rfqs, ...data.pos, ...data.vendors].filter(
          (i) => i.rag_status === 'red' || i.rag_status === 'amber',
        ).length
      : 0;
    return {
      content_key: contentKey ?? 'obeya_attention',
      status: (data ? 'loaded' : projectId ? 'loading' : 'idle') as 'loaded' | 'loading' | 'idle',
      query_key: projectId ? queryKey : null,
      text: `${needsAttention} item(s) need attention`,
      json: { project_id: projectId ?? null, needs_attention: needsAttention },
    };
  }, [contentKey, data, projectId, queryKey]));

  if (!projectId) return <Shell>Select a project</Shell>;
  if (isLoading || !data) return <Shell>Loading…</Shell>;

  const items = [...data.rfqs, ...data.pos, ...data.vendors]
    .filter((item) => item.rag_status === 'red' || item.rag_status === 'amber')
    .sort((a) => (a.rag_status === 'red' ? -1 : 1));

  return (
    <CardShell as="article" className="overflow-hidden h-full flex flex-col" style={{ fontSize: surfaceBase.rail }}>
      <header className="flex items-center gap-2 px-3 py-2 border-b border-border/60 shrink-0">
        <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
        <h3 className={text.heading}>
          Needs Attention
        </h3>
        <span className={text.muted}>
          {items.length}
        </span>
      </header>
      <div className="flex-1 overflow-y-auto p-2">
        {items.length === 0 ? (
          <div className={cn(text.muted, 'px-4 py-6 text-center italic')}>
            All clear — no issues detected
          </div>
        ) : (
          // Auto-fill grid — packs the wide rail with as many ~200px
          // tiles as fit (1 col on a narrow rail, 2–4 as it widens),
          // killing the horizontal whitespace a single-column list left.
          <div
            className="grid gap-1.5"
            style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))' }}
          >
            {items.slice(0, 24).map((item) => (
              <button
                key={item.semantic_id}
                onClick={() => selectNode({
                  id: item.semantic_id,
                  label: item.name || item.semantic_id,
                  type: item.entity_type.toLowerCase(),
                })}
                className={cn(
                  'text-left flex items-start gap-2 px-2.5 py-1.5 rounded-md border-l-2 hover:bg-accent/30 transition-colors',
                  item.rag_status === 'red' ? 'border-l-red-500 bg-red-500/5' : 'border-l-amber-500 bg-amber-500/5',
                )}
              >
                {item.rag_status === 'red' ? (
                  <AlertCircle className="w-3.5 h-3.5 text-red-400 mt-0.5 shrink-0" />
                ) : (
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-400 mt-0.5 shrink-0" />
                )}
                <div className="min-w-0 flex-1">
                  <p className={cn(text.title, 'truncate')}>{item.name || item.semantic_id}</p>
                  <p className={cn(text.muted, 'truncate')}>{item.entity_type} · {item.rag_label}</p>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </CardShell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <CardShell as="article" className="p-3" style={{ fontSize: surfaceBase.rail }}>
      <header className="flex items-center gap-2 mb-2">
        <span className="w-2 h-2 rounded-full bg-red-500" />
        <h3 className={text.heading}>
          Needs Attention
        </h3>
      </header>
      <div className={cn(text.muted, 'py-2')}>{children}</div>
    </CardShell>
  );
}
